import { useCallback, useMemo, useState } from "react";
import { MetaAudience } from "@common/interfaces/MetaTypes";
import { useLoading } from "src/Manager/hooks/form";
import actions from "src/actions";
import { MetaAudienceData } from "@common/interfaces";

// Types for useAudiences hook return value
export type UseAudiencesState = {
  audiences: MetaAudienceData[];
  loading: {
    audiences: boolean;
  };
};

export type UseAudiencesActions = {
  list: (params: { ad_account_id: string; next: boolean }) => Promise<void>;
  search: (params: { query: string; ad_account_id: string }) => Promise<void>;
};

export const useAudiences = (): {
  state: UseAudiencesState;
  actions: UseAudiencesActions;
} => {
  const [audiences, setAudiences] = useState<MetaAudienceData[]>([]);
  const [loading, setLoading] = useLoading({
    audiences: false,
  });

  const list = useCallback(
    ({ ad_account_id, next }: { ad_account_id: string; next: boolean }) => {
      setLoading("audiences", true);

      return actions.ADMIN.listAudiences({
        ad_account_id,
        limit: 5,
      })
        .then((res) => {
          setAudiences(
            res.audiences.data.map((audience) => ({
              ...audience,
              external_id: audience.id,
              audience_type: "meta",
            }))
          );
        })
        .finally(() => {
          setLoading("audiences", false);
        });
    },
    []
  );

  const search = useCallback(
    ({ query, ad_account_id }: { query: string; ad_account_id: string }) => {
      setLoading("audiences", true);
      return actions.ADMIN.searchAudiences({
        ad_account_id,
        query,
        limit: 5,
      })
        .then((res) => {
          setAudiences(
            res.audiences.data.map((audience) => ({
              ...audience,
              external_id: audience.id,
              audience_type: "meta",
            }))
          );
        })
        .finally(() => {
          setLoading("audiences", false);
        });
    },
    []
  );

  return {
    state: useMemo(
      () => ({
        audiences,
        loading,
      }),
      [audiences, loading]
    ),
    actions: useMemo(
      () => ({
        list,
        search,
      }),
      [list]
    ),
  };
};
